// Copyright (c) 2020 WiseTime. All rights reserved.

import React, { useMemo } from "react"
import { useSelector } from "react-redux"
import styled from "styled-components/macro"
import * as TimelineSlice from "../slices/timeline"
import ActionPaneTimeRows from "./ActionPaneTimeRows"
import Tips from "./Tips"
import * as TrashedTimeRowsSlice from "../trash/slice"
import { isMatchingToolbarAndSearchFilters } from "../filters"
import { selectToolbarState } from "features/timeline/toolbar/slice"
import { selectCurrentSiteId } from "../../user-context/slice"
import { getSortTypeStrategy } from "../sorting"
import {
  TimeBlocksSortOrder,
  TimelineRowSortType,
} from "../../../generated/timeline/toolbar/toolbar_pb"
import { ActionPaneWrapper } from "../../shared/styles"
import { RootState } from "../../../app/store"

const Wrapper = styled(ActionPaneWrapper)`
  position: relative;
  z-index: 100;
  overflow: initial;
  border-color: ${({ theme }): string => theme.colors.gainsboro};

  &:focus-within {
    z-index: 105;
  }

  @media (min-width: ${({ theme }): string => theme.screen.sm}) {
    min-height: 600px;
  }

  @media (max-width: ${({ theme }): string => theme.screen.sm}) {
    padding-top: 0;
    padding-bottom: 0;
  }
`

const ContentWrapper = styled.div`
  position: sticky;
  top: 0;
  padding: 1.8rem 0;

  @media (min-width: ${({ theme }): string => theme.screen.sm}) {
    margin-top: -18px;
  }

  @media (max-width: ${({ theme }): string => theme.screen.sm}) {
    padding: 0;
  }
`

type Props = {
  isTrashView?: boolean,
}

const ActionPane = ({ isTrashView = false }: Props): React.ReactElement | null => {
  const activeTimeRows = isTrashView
    ? useSelector(TrashedTimeRowsSlice.selectActiveTrashedTimeRows)
    : useSelector(TimelineSlice.selectActiveTimeRows)
  const toolbar = useSelector(selectToolbarState)
  const currentSiteId = useSelector(selectCurrentSiteId)
  const toolbarRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => e?.target.classList.toggle("is-sticky", e.intersectionRatio < 1),
      {
        rootMargin: "-1px 0px 0px 0px",
        threshold: [1],
      }
    )
    if (toolbarRef.current) {
      observer.observe(toolbarRef.current)
    }
  }, [toolbarRef.current])

  const timeRows = useMemo(() => {
    if (isTrashView) {
      // Don't apply filter in trash view
      return activeTimeRows
        .sort(getSortTypeStrategy(TimeBlocksSortOrder.ASCENDING, TimelineRowSortType.CHRONOLOGY))
    }
    return activeTimeRows
      .filter(timeRow => isMatchingToolbarAndSearchFilters(timeRow, toolbar, currentSiteId))
      .sort(getSortTypeStrategy(toolbar.timeBlocksSortOrder, toolbar.sortType))
  }, [isTrashView, activeTimeRows, toolbar, currentSiteId])

  const selectTaggedTimeRowsWithDifferentAssignee = useMemo(
    TimelineSlice.selectTaggedTimeRowsWithDifferentAssignee,
    [],
  )
  const hasTagWithDifferentAssignee = useSelector((state: RootState) =>
    selectTaggedTimeRowsWithDifferentAssignee(state, timeRows)
  )

  const renderSwitcher = () => {
    if (isTrashView) {
      return null
    }
    return (
      <Tips/>
    )
  }

  return (
    <Wrapper>
      <ContentWrapper>
        {timeRows.length ?
          <ActionPaneTimeRows
            timeRows={timeRows}
            hasTagWithDifferentAssignee={hasTagWithDifferentAssignee}
          />
          : <>{renderSwitcher()}</>
        }
      </ContentWrapper>
    </Wrapper>
  )
}

export default ActionPane
